import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import SheetTabs from './SheetTabs';
import TabSwitcher from './TabSwitcher';
import FilterPanel from './FilterPanel';
import OriginalSheetTable from './OriginalSheetTable';
import FilteredSheetTable from './FilteredSheetTable';
import ParsedResult from './ParsedResult';
import PreviewTable from './PreviewTable';

// const corsProxy = 'http://localhost:8080/proxy?url=';
const corsProxy = 'https://fifree-supabase-service.vercel.app/proxy?url=';

const defaultColumnKeys = [
  "index",
  "stock_code",
  "macode",
  "quantity",
  "market_price",
  "value",
  "portfolio_rate",
];

const ExcelFromUrlPreview = ({ fileUrl, onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(null);
  const [sheetStates, setSheetStates] = useState({});

  const updateSheetState = (sheetName, updates) => {
    setSheetStates(prev => ({
      ...prev,
      [sheetName]: { ...prev[sheetName], ...updates },
    }));
  };

  const removeEmptyRows = (data) =>
    data.filter(row =>
      row.some(cell => cell !== undefined && cell !== null && String(cell).trim() !== '')
    );

  useEffect(() => {
    if (!fileUrl) return;

    const parseExcel = async (buffer) => {
      const workbook = XLSX.read(buffer, { type: 'array' });
      const allSheets = workbook.SheetNames.map(name => {
        const worksheet = workbook.Sheets[name];
        const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        const data = removeEmptyRows(rawData);
        return { name, data };
      });
  
      const initialStates = {};
      allSheets.forEach(sheet => {
        initialStates[sheet.name] = {
          tab: 'original',
          fromRow: '',
          toRow: '',
          filteredRows: [],
          selectedRows: new Set(),
          columnKeys: sheet.data[0] || [],
        };
      });
  
      setSheets(allSheets);
      setSheetStates(initialStates);
      setActiveSheet(allSheets[0]?.name);
    };

    const loadFile = async () => {
      try {
        setLoading(true);
        const res = await fetch(corsProxy + fileUrl);
        const contentType = res.headers.get('content-type');

        if (contentType.includes('application/json')) {
          const json = await res.json();
          if (json.type === 'rar' && json.files?.length) {
            const buffer = Uint8Array.from(atob(json.files[0].data), c => c.charCodeAt(0));
            await parseExcel(buffer);
          }
        } else {
          const buffer = await res.arrayBuffer();
          await parseExcel(buffer);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    
    loadFile();
  }, [fileUrl]);

  if (!activeSheet || !sheets.length) return null;

  const activeData = sheets.find(s => s.name === activeSheet);
  const {
    tab,
    fromRow,
    toRow,
    filteredRows,
    selectedRows,
    columnKeys
  } = sheetStates[activeSheet] || {};

  const handleFilter = () => {
    const start = fromRow ? parseInt(fromRow) - 1 : 0;
    const end = toRow ? parseInt(toRow) - 1 : activeData.data.length - 1;
    const rows = activeData.data.slice(start, end + 1);

    const firstRowLength = rows[0]?.length || 0;
    const columnKeys =
      defaultColumnKeys.slice(0, firstRowLength).concat(
        Array(Math.max(0, firstRowLength - defaultColumnKeys.length)).fill("")
      );

    updateSheetState(activeSheet, {
      filteredRows: rows,
      tab: 'filtered',
      selectedRows: new Set(),
      columnKeys,
    });
  };

  const updateSelectedRows = (index) => {
    const current = new Set(selectedRows || []);
    current.has(index) ? current.delete(index) : current.add(index);
    updateSheetState(activeSheet, { selectedRows: current });
  };

  const updateColumnKey = (index, newKey) => {
    const newKeys = [...columnKeys];
    newKeys[index] = newKey;
    updateSheetState(activeSheet, { columnKeys: newKeys });
  };

  return (
    <Spin spinning={loading} indicator={<LoadingOutlined spin />} tip="Loading...">
      <SheetTabs sheets={sheets} active={activeSheet} onSelect={setActiveSheet} />
      <TabSwitcher
        tab={tab}
        setTab={(val) => updateSheetState(activeSheet, { tab: val })}
        hasFiltered={filteredRows.length > 0}
      />
      {tab === 'original' && (
        <>
          <FilterPanel
            fromRow={fromRow}
            toRow={toRow}
            setFromRow={(val) => updateSheetState(activeSheet, { fromRow: val })}
            setToRow={(val) => updateSheetState(activeSheet, { toRow: val })}
            handleFilter={handleFilter}
          />
          <OriginalSheetTable data={activeData.data} />
        </>
      )}

      {tab === 'filtered' && (
        <FilteredSheetTable
          data={filteredRows}
          selectedRows={selectedRows}
          updateSelectedRows={(i) => updateSelectedRows(i)}
          updateAllSelection={(newSet) =>
            updateSheetState(activeSheet, { selectedRows: newSet })
          }
          columnKeys={columnKeys}
          updateColumnKey={updateColumnKey}
          onParse={() => updateSheetState(activeSheet, { tab: 'parsed' })}
        />
      )}

      {tab === 'parsed' && (
        <ParsedResult
          data={filteredRows}
          selectedRows={selectedRows}
          columnKeys={columnKeys}
          onPreview={() => updateSheetState(activeSheet, { tab: 'preview' })}
        />
      )}

      {tab === 'preview' && (
        <PreviewTable onSubmit={onSubmit} data={filteredRows} selectedRows={selectedRows} columnKeys={columnKeys}/>
      )}

    </Spin>
  );
};

export default ExcelFromUrlPreview;
